import { useState, useEffect, useCallback, useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  FolderSimple, CaretRight, ArrowLeft, Plus, Trash, UploadSimple,
  CircleNotch, Images, PencilSimple, Warning, StackPlus,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { BulkStyleUploader } from "./BulkStyleUploader";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Reusable Styles Library ("Looks") browser: Category -> Subcategory -> thumbnails.
// admin=true  -> can upload / delete styles.
// manage=true -> can add / rename / delete categories & subcategories (structure editing).
export function StylesLibraryBrowser({ admin = false, manage = false, onSelect, selectLabel = "Use this style" }) {
  const [tax, setTax] = useState([]);
  const [cat, setCat] = useState(null);
  const [sub, setSub] = useState(null);
  const [styles, setStyles] = useState([]);
  const [loadingStyles, setLoadingStyles] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [upCat, setUpCat] = useState("");
  const [upSub, setUpSub] = useState("");
  const [upTitle, setUpTitle] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const upFileRef = useRef(null);

  // Structure-management dialogs
  const [nameModal, setNameModal] = useState({ open: false, mode: "", catId: null, subId: null, value: "", saving: false });
  const [confirm, setConfirm] = useState({ open: false, kind: "", catId: null, subId: null, name: "", count: 0, deleting: false });

  const loadTax = useCallback(async () => {
    try {
      const r = await api.get("/library/taxonomy");
      setTax(r.data?.categories || []);
    } catch (e) { /* browse gracefully degrades */ }
  }, []);
  // Raw fetch (bypasses local state) — used by BulkStyleUploader so it can
  // read the freshest taxonomy right after creating a category/subcategory.
  const loadTaxRaw = useCallback(async () => {
    try {
      const r = await api.get("/library/taxonomy");
      const t = r.data?.categories || [];
      setTax(t);
      return t;
    } catch (e) { return []; }
  }, []);
  useEffect(() => { loadTax(); }, [loadTax]);

  const loadStyles = useCallback(async (c, s) => {
    setLoadingStyles(true);
    try {
      const r = await api.get("/library/styles", { params: { category: c, subcategory: s } });
      setStyles(r.data || []);
    } catch (e) { setStyles([]); }
    finally { setLoadingStyles(false); }
  }, []);

  const openSubcategory = (catName, subName) => { setCat(catName); setSub(subName); loadStyles(catName, subName); };
  const backToCategories = () => { setCat(null); setSub(null); setStyles([]); };
  const backToSubcategories = () => { setSub(null); setStyles([]); };

  const activeCat = tax.find((c) => c.name === cat);
  const upSubOptions = tax.find((c) => c.name === upCat)?.subcategories || [];

  const openUpload = () => {
    setUpCat(cat || tax[0]?.name || ""); setUpSub(sub || ""); setUpTitle(""); setUpFile(null); setUploadOpen(true);
  };

  useEffect(() => {
    if (!upCat) return;
    const subs = tax.find((c) => c.name === upCat)?.subcategories || [];
    if (subs.length && !subs.some((s) => s.name === upSub)) setUpSub(subs[0].name);
  }, [upCat, tax]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitUpload = async () => {
    if (!upCat || !upSub) { toast.error("Pick a category and subcategory."); return; }
    if (!upTitle.trim()) { toast.error("Give the style a title."); return; }
    if (!upFile) { toast.error("Choose an image to upload."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile); fd.append("category", upCat); fd.append("subcategory", upSub); fd.append("title", upTitle.trim());
      await api.post("/library/styles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Style added to the library.");
      setUploadOpen(false);
      await loadTax();
      if (cat === upCat && sub === upSub) await loadStyles(upCat, upSub);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setUploading(false); }
  };

  const deleteStyle = async (id) => {
    try {
      await api.delete(`/library/styles/${id}`);
      setStyles((prev) => prev.filter((s) => s.id !== id));
      loadTax();
      toast.success("Style removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove style."); }
  };

  // ---- Structure management ----
  const openNameModal = (mode, { catId = null, subId = null, value = "" } = {}) =>
    setNameModal({ open: true, mode, catId, subId, value, saving: false });

  const submitName = async () => {
    const val = nameModal.value.trim();
    if (!val) { toast.error("Please enter a name."); return; }
    setNameModal((m) => ({ ...m, saving: true }));
    try {
      if (nameModal.mode === "add-cat") {
        await api.post("/library/categories", { name: val });
        toast.success(`Category “${val}” added.`);
      } else if (nameModal.mode === "rename-cat") {
        await api.put(`/library/categories/${nameModal.catId}`, { name: val });
        if (activeCat?.id === nameModal.catId) setCat(val);
        toast.success("Category renamed.");
      } else if (nameModal.mode === "add-sub") {
        await api.post(`/library/categories/${nameModal.catId}/subcategories`, { name: val });
        toast.success(`Subcategory “${val}” added.`);
      } else if (nameModal.mode === "rename-sub") {
        const oldName = activeCat?.subcategories?.find((s) => s.id === nameModal.subId)?.name;
        await api.put(`/library/categories/${nameModal.catId}/subcategories/${nameModal.subId}`, { name: val });
        if (sub && sub === oldName) { setSub(val); loadStyles(cat, val); }
        toast.success("Subcategory renamed.");
      }
      setNameModal({ open: false, mode: "", catId: null, subId: null, value: "", saving: false });
      await loadTax();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Please try again.");
      setNameModal((m) => ({ ...m, saving: false }));
    }
  };

  const askDelete = (payload) => setConfirm({ open: true, deleting: false, ...payload });

  const doDelete = async () => {
    setConfirm((c) => ({ ...c, deleting: true }));
    try {
      if (confirm.kind === "cat") {
        await api.delete(`/library/categories/${confirm.catId}`);
        if (activeCat?.id === confirm.catId) backToCategories();
        toast.success("Category deleted.");
      } else {
        await api.delete(`/library/categories/${confirm.catId}/subcategories/${confirm.subId}`);
        if (sub && sub === confirm.name) backToSubcategories();
        toast.success("Subcategory deleted.");
      }
      setConfirm({ open: false, kind: "", catId: null, subId: null, name: "", count: 0, deleting: false });
      await loadTax();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete. Please try again.");
      setConfirm((c) => ({ ...c, deleting: false }));
    }
  };

  const IconBtn = ({ onClick, testid, title, danger, children }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={testid}
      title={title}
      className={`h-7 w-7 rounded-md flex items-center justify-center bg-black/50 backdrop-blur transition-colors ${danger ? "hover:bg-red-600" : "hover:bg-white/20"} text-white`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="styles-library">
      {/* Breadcrumb + actions */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <button onClick={backToCategories} data-testid="library-breadcrumb-root" className={`font-semibold transition-colors ${cat ? "text-zinc-400 hover:text-white" : "text-white"}`}>All Styles</button>
          {cat && (<>
            <CaretRight size={13} className="text-zinc-600 shrink-0" />
            <button onClick={backToSubcategories} data-testid="library-back-btn" className={`font-semibold transition-colors truncate ${sub ? "text-zinc-400 hover:text-white" : "text-white"}`}>{cat}</button>
          </>)}
          {sub && (<>
            <CaretRight size={13} className="text-zinc-600 shrink-0" />
            <span className="font-semibold text-white truncate">{sub}</span>
          </>)}
        </div>
        <div className="flex items-center gap-2">
          {manage && !cat && (
            <button onClick={() => openNameModal("add-cat")} data-testid="library-add-category-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors">
              <Plus weight="bold" size={15} /> New category
            </button>
          )}
          {manage && cat && !sub && activeCat && (
            <button onClick={() => openNameModal("add-sub", { catId: activeCat.id })} data-testid="library-add-subcategory-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors">
              <Plus weight="bold" size={15} /> New subcategory
            </button>
          )}
          {admin && (
            <button onClick={() => { setUploadOpen(false); setBulkOpen((v) => !v); }} data-testid="library-bulk-upload-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors">
              <StackPlus weight="bold" size={15} /> Bulk upload
            </button>
          )}
          {admin && (
            <button onClick={openUpload} data-testid="library-upload-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-semibold transition-colors">
              <Plus weight="bold" size={15} /> Upload style
            </button>
          )}
        </div>
      </div>

      {/* Admin upload panel */}
      {admin && uploadOpen && (
        <div data-testid="library-upload-panel" className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/[0.06] p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Category</label>
              <select data-testid="library-upload-category" value={upCat} onChange={(e) => setUpCat(e.target.value)} className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60">
                {tax.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Subcategory</label>
              <select data-testid="library-upload-subcategory" value={upSub} onChange={(e) => setUpSub(e.target.value)} className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60">
                {upSubOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Title</label>
            <input data-testid="library-upload-title" value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="e.g. Neon Midnight Party" className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60" />
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button onClick={() => upFileRef.current?.click()} data-testid="library-upload-pick" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-dashed border-white/20 hover:border-fuchsia-500/60 text-sm font-medium text-zinc-300 hover:text-white transition-colors">
              <UploadSimple size={16} weight="bold" /> {upFile ? "Change image" : "Choose image"}
            </button>
            {upFile && <span className="text-xs text-zinc-400 truncate max-w-[220px]">{upFile.name}</span>}
            <input ref={upFileRef} type="file" accept="image/*" className="hidden" data-testid="library-upload-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) setUpFile(f); e.target.value = ""; }} />
          </div>
          {upFile && <div className="mt-3"><img src={URL.createObjectURL(upFile)} alt="preview" className="max-h-40 rounded-lg ring-1 ring-white/10 object-contain" /></div>}
          <div className="mt-4 flex items-center gap-2">
            <button onClick={submitUpload} disabled={uploading} data-testid="library-upload-submit" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-bold transition-colors disabled:opacity-60">
              {uploading ? <><CircleNotch size={16} className="animate-spin" /> Uploading…</> : <>Add to library</>}
            </button>
            <button onClick={() => setUploadOpen(false)} className="h-10 px-4 rounded-full text-sm font-medium text-zinc-400 hover:text-white transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Admin bulk upload panel */}
      {admin && bulkOpen && (
        <BulkStyleUploader
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          tax={tax}
          loadTaxRaw={loadTaxRaw}
          currentCat={cat || ""}
          currentSub={sub || ""}
          onDone={async ({ ok, cat: uCat, sub: uSub }) => {
            if (ok > 0) {
              await loadTaxRaw();
              if (cat === uCat && sub === uSub) await loadStyles(uCat, uSub);
            }
          }}
        />
      )}

      {/* Level 1 — categories */}
      {!cat && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="library-categories">
          {tax.length === 0 ? (
            <p className="col-span-full text-sm text-zinc-500 py-10 text-center">Loading library…</p>
          ) : tax.map((c) => (
            <div key={c.id} data-testid={`library-category-${slug(c.name)}`} onClick={() => { setCat(c.name); setSub(null); }} className="group relative rounded-xl border border-white/10 bg-[#121212] p-5 text-left hover:border-fuchsia-500/50 hover:bg-[#161616] transition-all cursor-pointer">
              <div className="h-11 w-11 rounded-lg bg-fuchsia-600/15 border border-fuchsia-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FolderSimple weight="fill" size={22} className="text-fuchsia-400" />
              </div>
              <p className="font-semibold text-sm">{c.name}</p>
              <p className="text-xs text-zinc-500">{c.count} {c.count === 1 ? "style" : "styles"}</p>
              {manage && (
                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconBtn testid={`library-cat-edit-${c.id}`} title="Rename category" onClick={() => openNameModal("rename-cat", { catId: c.id, value: c.name })}><PencilSimple size={13} /></IconBtn>
                  <IconBtn testid={`library-cat-delete-${c.id}`} title="Delete category" danger onClick={() => askDelete({ kind: "cat", catId: c.id, name: c.name, count: c.count })}><Trash size={13} /></IconBtn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Level 2 — subcategories */}
      {cat && !sub && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="library-subcategories">
          {(activeCat?.subcategories || []).map((s) => (
            <div key={s.id} data-testid={`library-subcategory-${slug(s.name)}`} onClick={() => openSubcategory(cat, s.name)} className="group relative rounded-xl border border-white/10 bg-[#121212] p-5 text-left hover:border-fuchsia-500/50 hover:bg-[#161616] transition-all cursor-pointer">
              <div className="h-11 w-11 rounded-lg bg-blue-600/15 border border-blue-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Images weight="fill" size={22} className="text-blue-400" />
              </div>
              <p className="font-semibold text-sm">{s.name}</p>
              <p className="text-xs text-zinc-500">{s.count} {s.count === 1 ? "style" : "styles"}</p>
              {manage && activeCat && (
                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconBtn testid={`library-sub-edit-${s.id}`} title="Rename subcategory" onClick={() => openNameModal("rename-sub", { catId: activeCat.id, subId: s.id, value: s.name })}><PencilSimple size={13} /></IconBtn>
                  <IconBtn testid={`library-sub-delete-${s.id}`} title="Delete subcategory" danger onClick={() => askDelete({ kind: "sub", catId: activeCat.id, subId: s.id, name: s.name, count: s.count })}><Trash size={13} /></IconBtn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Level 3 — style thumbnails */}
      {cat && sub && (
        <div data-testid="library-styles-grid">
          <button onClick={backToSubcategories} className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={14} weight="bold" /> Back to {cat}
          </button>
          {loadingStyles ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="rounded-xl bg-white/5 animate-pulse aspect-[4/5]" />)}
            </div>
          ) : styles.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 py-16 text-center text-zinc-500">
              No styles in “{cat} → {sub}” yet.{admin ? " Use “Upload style” to add one." : ""}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {styles.map((st) => (
                <div key={st.id}>
                  <div data-testid={`library-style-${st.id}`} className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#121212] aspect-[4/5]">
                    <img src={fileUrl(st.storage_path)} alt={st.title} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-end p-4">
                      <button onClick={() => onSelect?.(st)} data-testid={`library-select-${st.id}`} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-semibold transition-colors">
                        <Plus weight="bold" size={15} /> {selectLabel}
                      </button>
                    </div>
                    {admin && (
                      <button onClick={() => deleteStyle(st.id)} data-testid={`library-delete-${st.id}`} title="Delete style" className="absolute top-2 right-2 h-7 w-7 rounded-md bg-black/60 backdrop-blur hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <Trash size={14} className="text-white" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate mt-2 px-0.5" title={st.title}>{st.title}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add / Rename dialog */}
      {manage && (
        <Dialog open={nameModal.open} onOpenChange={(o) => !o && setNameModal((m) => ({ ...m, open: false }))}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="library-name-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg">
                {nameModal.mode === "add-cat" && "New category"}
                {nameModal.mode === "rename-cat" && "Rename category"}
                {nameModal.mode === "add-sub" && "New subcategory"}
                {nameModal.mode === "rename-sub" && "Rename subcategory"}
              </DialogTitle>
            </DialogHeader>
            <input
              autoFocus
              data-testid="library-name-input"
              value={nameModal.value}
              onChange={(e) => setNameModal((m) => ({ ...m, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") submitName(); }}
              placeholder="Enter a name"
              className="w-full h-11 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60"
            />
            <DialogFooter>
              <button onClick={() => setNameModal((m) => ({ ...m, open: false }))} className="h-10 px-4 rounded-full text-sm font-medium text-zinc-400 hover:text-white transition-colors">Cancel</button>
              <button onClick={submitName} disabled={nameModal.saving} data-testid="library-name-save" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-bold transition-colors disabled:opacity-60">
                {nameModal.saving ? <CircleNotch size={16} className="animate-spin" /> : null} Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation */}
      {manage && (
        <Dialog open={confirm.open} onOpenChange={(o) => !o && setConfirm((c) => ({ ...c, open: false }))}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="library-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg flex items-center gap-2">
                <Warning size={20} weight="fill" className="text-amber-400" /> Delete {confirm.kind === "cat" ? "category" : "subcategory"}?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-300">
              You're about to delete <span className="font-semibold text-white">“{confirm.name}”</span>.
              {confirm.count > 0 ? (
                <span className="block mt-2 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-red-200">
                  Warning: this contains <span className="font-bold">{confirm.count}</span> {confirm.count === 1 ? "style" : "styles"}
                  {confirm.kind === "cat" ? " across its subcategories" : ""}. They will be permanently deleted too.
                </span>
              ) : <span className="block mt-1 text-zinc-500">This can’t be undone.</span>}
            </p>
            <DialogFooter>
              <button onClick={() => setConfirm((c) => ({ ...c, open: false }))} className="h-10 px-4 rounded-full text-sm font-medium text-zinc-400 hover:text-white transition-colors">Cancel</button>
              <button onClick={doDelete} disabled={confirm.deleting} data-testid="library-confirm-delete" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-bold transition-colors disabled:opacity-60">
                {confirm.deleting ? <CircleNotch size={16} className="animate-spin" /> : <Trash size={16} />} Delete
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
